/**
 * Determine whether a Tiptap document (or stringified doc) contains any visible text.
 * Accepts:
 * - undefined/null → false
 * - plain string → true if non-empty after trim
 * - JSON string representing Tiptap doc → true if any text node has non-whitespace
 * - Tiptap doc object → same as above
 */
import type { JSONContent } from '@tiptap/core';

export type TiptapJSON = JSONContent;

export function tiptapDocHasText(
  value: string | TiptapJSON | null | undefined
): value is string | TiptapJSON {
  if (value === null || value === undefined) return false;

  const nodeHasText = (node: any): boolean => {
    if (!node) return false;

    if (node.type === 'text' && typeof node.text === 'string' && node.text.trim().length > 0) {
      return true;
    }

    if (Array.isArray(node.content)) {
      return node.content.some(nodeHasText);
    }

    return false;
  };

  // Strings: either plain text or JSON-encoded Tiptap doc
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.length === 0) return false;

    try {
      const maybeDoc = trimmed.startsWith('{') ? (JSON.parse(trimmed) as JSONContent | null) : null;

      if (maybeDoc && typeof maybeDoc === 'object' && (maybeDoc as any).type === 'doc') {
        const content = Array.isArray(maybeDoc.content) ? maybeDoc.content : [];

        return content.some(nodeHasText);
      }

      // Plain non-empty string
      return true;
    } catch {
      // Non-JSON non-empty string
      return true;
    }
  }

  // Objects: try to interpret as Tiptap doc
  if (typeof value === 'object') {
    const doc = value as JSONContent;

    if (doc && (doc as any).type === 'doc') {
      const content = Array.isArray(doc.content) ? doc.content : [];

      return content.some(nodeHasText);
    }
  }

  // Fallback: treat other truthy values as true
  return !!value;
}
